import React, {useEffect, useMemo, useState} from "react";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import PageHeader from "../../components/page-header";
import {PlatformTenantLinkApi} from "../../../models/crm/crm-api";
import {useTranslate} from "../../../system/useTranslate";
import {useWorkspaceRole} from "../../../system/use-workspace-role";

const tenantLinkApi = new PlatformTenantLinkApi();

function PlatformConnection() {
    const translate = useTranslate();
    const {role} = useWorkspaceRole();
    const [targets, setTargets] = useState([]);
    const [selectedTarget, setSelectedTarget] = useState(null);
    const [link, setLink] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        let active = true;
        tenantLinkApi.targets().then((response) => {
            if (!active) return;
            const rows = Array.isArray(response?.data) ? response.data : [];
            setTargets(rows);
            setSelectedTarget(rows.length === 1 ? rows[0]?.documentId : null);
        }).catch((error) => {
            if (active) setMessage({severity: "error", key: error?.message || "platform-connection.error.load"});
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, []);

    const targetOptions = useMemo(() => targets.map((target) => ({
        label: target.name,
        value: target.documentId,
    })), [targets]);

    const issue = async () => {
        if (!selectedTarget) return;
        setSubmitting(true);
        setMessage(null);
        try {
            const response = await tenantLinkApi.issue(selectedTarget);
            setLink(response?.data || null);
            setMessage({severity: "success", key: "platform-connection.code-created"});
        } catch (error) {
            setMessage({severity: "error", key: error?.message || "platform-connection.error.issue"});
        } finally {
            setSubmitting(false);
        }
    };

    const copy = async () => {
        if (!link?.linkCode) return;
        try {
            await navigator.clipboard.writeText(link.linkCode);
            setMessage({severity: "success", key: "platform-connection.code-copied"});
        } catch {
            setMessage({severity: "error", key: "platform-connection.error.copy"});
        }
    };

    if (role !== "owner") return null;

    return (
        <div className="flex flex-column p-4 gap-4 overflow-auto h-full">
            <PageHeader
                title="platform-connection"
                description="platform-connection-description"
                titleClassName="text-lg"
                descriptionClassName="text-xs"
            />
            <div className="surface-card border-1 surface-border border-round p-4 flex flex-column gap-3 w-full lg:w-8">
                <span className="font-semibold text-sm">{translate("platform-connection.generate-title")}</span>
                <small className="text-color-secondary text-xs line-height-3">{translate("platform-connection.generate-description")}</small>
                {message && <Message severity={message.severity} text={translate(message.key)} className="w-full text-xs" />}
                {!loading && !targets.length && (
                    <Message severity="warn" text={translate("platform-connection.no-targets")} className="w-full text-xs" />
                )}
                {targets.length > 1 && (
                    <div className="flex flex-column gap-2">
                        <label htmlFor="platform-target" className="text-xs">{translate("platform-connection.target")}</label>
                        <Dropdown
                            inputId="platform-target"
                            value={selectedTarget}
                            options={targetOptions}
                            onChange={(event) => setSelectedTarget(event.value)}
                            placeholder={translate("platform-connection.target-placeholder")}
                            className="w-full text-xs"
                            panelClassName="text-xs"
                            pt={{root: {dir: "rtl"}, panel: {dir: "rtl"}}}
                        />
                    </div>
                )}
                <Button
                    label={translate("platform-connection.generate")}
                    loading={loading || submitting}
                    disabled={!selectedTarget || loading || submitting}
                    onClick={issue}
                    className="align-self-start text-xs"
                />
                {link?.linkCode && (
                    <div className="flex flex-column gap-2 border-top-1 surface-border pt-3">
                        <label htmlFor="platform-link-code" className="text-xs font-semibold">{translate("platform-connection.code")}</label>
                        <div className="flex flex-column md:flex-row gap-2">
                            <InputText id="platform-link-code" value={link.linkCode} readOnly className="flex-1 text-xs" dir="ltr" />
                            <Button label={translate("copy")} outlined onClick={copy} className="text-xs white-space-nowrap" />
                        </div>
                        <small className="text-color-secondary text-xs">
                            {translate("platform-connection.expires-at")} {new Date(link.expiresAt).toLocaleString()}
                        </small>
                        <Message severity="warn" text={translate("platform-connection.one-time-warning")} className="w-full text-xs" />
                    </div>
                )}
            </div>
        </div>
    );
}

export default PlatformConnection;
